import { onBeforeUnmount, onMounted, unref, watch } from 'vue';

import { noop } from '@components/utils';

import type { Ref } from 'vue';

const SMOOTH_PTG = 14 / 15;

export function useMobileTouchMove(
  inVirtual: Ref<boolean>,
  listRef: Ref<HTMLDivElement | undefined>,
  callback: (offsetY: number, smoothOffset?: boolean) => boolean
) {
  let touched = false;
  let touchY = 0;

  let element: HTMLElement | null = null;

  // Smooth scroll
  let interval: any = null;

  const cleanUpEvents = () => {
    if (element) {
      element.removeEventListener('touchmove', onTouchMove);
      element.removeEventListener('touchend', onTouchEnd);
    }
  };

  function onTouchMove(event: TouchEvent) {
    if (touched) {
      const currentY = Math.ceil(event.touches[0].pageY);
      let offsetY = touchY - currentY;
      touchY = currentY;

      if (callback(offsetY)) {
        event.preventDefault();
      }

      // Smooth interval
      clearInterval(interval);
      interval = setInterval(() => {
        offsetY *= SMOOTH_PTG;

        if (!callback(offsetY, true) || Math.abs(offsetY) <= 0.1) {
          clearInterval(interval);
        }
      }, 16);
    }
  }

  function onTouchEnd() {
    touched = false;

    cleanUpEvents();
  }

  function onTouchStart(event: TouchEvent) {
    cleanUpEvents();

    if (event.touches.length === 1 && !touched) {
      touched = true;
      touchY = Math.ceil(event.touches[0].pageY);

      element = event.target as HTMLElement;
      element!.addEventListener('touchmove', onTouchMove, { passive: false });
      element!.addEventListener('touchend', onTouchEnd);
    }
  }

  onMounted(() => {
    document.addEventListener('touchmove', noop, { passive: false });

    watch(
      inVirtual,
      (value) => {
        unref(listRef)?.removeEventListener('touchstart', onTouchStart);
        cleanUpEvents();
        clearInterval(interval);

        if (value) {
          unref(listRef)?.addEventListener('touchstart', onTouchStart, { passive: false });
        }
      },
      { immediate: true }
    );
  });

  onBeforeUnmount(() => {
    document.removeEventListener('touchmove', noop);
  });
}
